import { orderBy } from "lodash-es";

import i18n from "i18next";
import clsx from "clsx";
import { EContactType } from "src/pages/Sources/AddContactDetailsModal";
export const DEFAULT_LANGUAGE = "en";

export const isPwa = () => {
  return ["fullscreen", "standalone", "minimal-ui"].some(
    (displayMode) => matchMedia(`(display-mode: ${displayMode})`).matches,
  );
};

export const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

export const FILE_STORAGE_SERVER_URL = "https://media.expenser.net";

export const PAYMENT_METHODS = () =>
  orderBy(
    [
      {
        name: i18n.t("common:payment_methods.card"),
        id: "CARD",
        settings: {
          bg: { r: 28, g: 216, b: 236, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.transfer"),
        id: "TRANSFER",
        settings: {
          bg: { r: 247, g: 90, b: 158, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.cash"),
        id: "CASH",
        settings: {
          bg: { r: 150, g: 209, b: 17, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.wallet"),
        id: "WALLET",
        settings: {
          bg: { r: 182, g: 135, b: 53, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.check"),
        id: "CHECK",
        settings: {
          bg: { r: 74, g: 37, b: 198, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.phone"),
        id: "PHONE",
        settings: {
          bg: { r: 184, g: 16, b: 134, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.digital"),
        id: "DIGITAL",
        settings: {
          bg: { r: 0, g: 0, b: 0, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.prepaid_card"),
        id: "PREPAID_CARD",
        settings: {
          bg: { r: 0, g: 0, b: 0, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
      {
        name: i18n.t("common:payment_methods.contactless"),
        id: "CONTACTLESS",
        settings: {
          bg: { r: 3, g: 99, b: 220, a: 1 },
          color: { r: 255, g: 255, b: 255, a: 1 },
        },
      },
    ],
    ["name"],
    ["asc"],
  );

export const TRANSACTION_STATUSES = () => [
  {
    id: "PENDING",
    name: i18n.t("misc:status.pending"),
    className: clsx(["bg-red-500", "text-red-50"]),
    settings: {
      color: {
        r: 250,
        g: 15,
        b: 54,
        a: 1,
      },
      bg: {
        r: 250,
        g: 15,
        b: 54,
        a: 1,
      },
    },
  },
  {
    id: "NULL",
    name: i18n.t("misc:status.null"),
    className: clsx(["bg-gray-100", "text-gray-500"]),
    settings: {
      color: {
        r: 250,
        g: 15,
        b: 54,
        a: 1,
      },
      bg: {
        r: 75,
        g: 85,
        b: 99,
        a: 1,
      },
    },
  },
  {
    id: "PAID",
    name: i18n.t("misc:status.paid"),
    className: clsx(["bg-green-500", "text-green-50"]),
    settings: {
      color: {
        r: 74,
        g: 222,
        b: 128,
        a: 1,
      },
      bg: {
        r: 41,
        g: 224,
        b: 15,
        a: 1,
      },
    },
  },
];

export const PLANS = {
  FREE: {
    name: "Gratuito",
    permissions: [],
  },
  HOME: {
    name: "Hogar",
    permissions: [],
  },
  SMALLBUSINESS: {
    name: "Empresa",
    permissions: [],
  },
};

export enum ETransactionType {
  INCOME = "INCOME",
  EXPENSE = "EXPENSE",
  LOAN = "LOAN",
  TRANSFER = "TRANSFER",
}

export enum ELoanType {
  RECEIVED = "RECEIVED",
  GIVEN = "GIVEN",
}

export enum EIncomeType {
  CASH = "CASH",
  CREDIT = "CREDIT",
}

export enum EExpenseType {
  CASH = "CASH",
  CREDIT = "CREDIT",
}

interface ITransactionSubType {
  ID: string;
  NAME: string;
}

interface ITransactionCategory<T extends string> {
  ID: T;
  NAME: string;
  TYPES: Record<string, ITransactionSubType>;
}

interface ITransactionWithoutCategory<T extends string> {
  ID: T;
  NAME: string;
  TYPES: boolean;
}

export type ETransactionTypes = {
  [ETransactionType.INCOME]: ITransactionCategory<ETransactionType.INCOME>;
  [ETransactionType.EXPENSE]: ITransactionCategory<ETransactionType.EXPENSE>;
  [ETransactionType.TRANSFER]: ITransactionWithoutCategory<ETransactionType.TRANSFER>;
};

export const TRANSACTION_TYPES: ETransactionTypes = {
  [ETransactionType.INCOME]: {
    ID: ETransactionType.INCOME,
    NAME: "Ingreso",
    TYPES: {
      [EIncomeType.CASH]: {
        ID: EIncomeType.CASH,
        NAME: "Contado",
      },
      [EIncomeType.CREDIT]: {
        ID: EIncomeType.CREDIT,
        NAME: "Crédito",
      },
    },
  },
  [ETransactionType.EXPENSE]: {
    ID: ETransactionType.EXPENSE,
    NAME: "Gasto",
    TYPES: {
      [EExpenseType.CASH]: {
        ID: EExpenseType.CASH,
        NAME: "Contado",
      },
      [EExpenseType.CREDIT]: {
        ID: EExpenseType.CREDIT,
        NAME: "Crédito",
      },
    },
  },
  [ETransactionType.TRANSFER]: {
    ID: ETransactionType.TRANSFER,
    NAME: "Transferencia",
    TYPES: false,
  },
};

export const PAGINATION_ROWS_PER_PAGE_OPTIONS = [
  10, 25, 50, 100, 150, 200, 250,
];
export const [PAGINATION_MIN] = PAGINATION_ROWS_PER_PAGE_OPTIONS;

export const MOBILE_BREAKPOINT = 767;
export const TABLET_BREAKPOINT = 1023;
export const DESKTOP_BREAKPOINT = 1279;

export const INVOICE_NOTES = [
  `Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla felis dui,`,
  `luctus ultrices ante quis, dignissim sodales tellus.Donec porttitor`,
  `dolor non nibh interdum venenatis.`,
].join("\n");

export const INVITE_STATUS_SENT = "SENT";

export const CATEGORIES_DEFAULT_TEXT_COLOR = { r: 255, g: 255, b: 255, a: 1 };
export const CATEGORIES_DEFAULT_BG_COLOR = { r: 0, g: 0, b: 0, a: 1 };
export const CATEGORIES_DEFAULT_ICON = "MdCategory";

export const SOURCES_DEFAULT_TEXT_COLOR = { r: 255, g: 255, b: 255, a: 1 };
export const SOURCES_DEFAULT_BG_COLOR = { r: 0, g: 0, b: 0, a: 1 };
export const SOURCES_DEFAULT_ICON = "MdPerson";

export const TOAST_TEXT_COLOR = { r: 255, g: 255, b: 255, a: 1 };
export const TOAST_BG_COLOR = { r: 0, g: 0, b: 0, a: 0.8 };

export const MOBILE_BREAKPOINT_MEDIA_QUERY = "(max-width: 767px)";

export enum EToggleView {
  CHART = "chart",
  DATA = "data",
  ALL = "all",
}

export enum EDataView {
  TABLE = "table",
  CARDS = "cards",
}

export const PAGINATION_PAGE_SIZES = [5, 10, 15, 20, 25, 30];
export const MOBILE_CARDS_PAGE_SIZE = 5;
export const DESKTOP_CARDS_PAGE_SIZE = 10;
export const DESKTOP_CARDS_NO_PANEL_PAGE_SIZE = 12;

export const CONTACT_DEFAULT_ICONS = {
  [EContactType.EMAIL]: "MdOutlineEmail",
  [EContactType.PHONE]: "MdOutlinePhone",
  [EContactType.LINK]: "MdOutlineLink",
  [EContactType.SMS]: "MdOutlineChat",
};

export const COUNT_UP_ANIMATION_DURATION = 0.5;

export const FEEDBACK_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSeU9aboWjne4i_Rf-FkehOaFUGx2QKXOOwiQS4idJc8QJsnEw/viewform?usp=header";

export const SUPPORT_URL = "https://www.expenser.net/";

export const CHART_BORDER_RADIUS = 6;
export const ALLOW_SWIPE_IN_DESKTOP = true;
export const REGISTER_ACCOUNT_IS_ENABLED = true;
export const ASK_CONFIRMATION_ON_SAVE_OR_UPDATE = true;
